import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { tierDisplayName } from '../utils/tierNames';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle 
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import { TagChip, TagFilter } from '../components/TagInput';
import BatchTagModal from '../components/BatchTagModal';
import NotificationBell from '../components/NotificationBell';
import AppShell from '../components/AppShell';
import VideoMixPlayer from '../components/VideoMixPlayer';
import ExportPublishPanel from '../components/ExportPublishPanel';
import { VolumeControl, AudioMixPanel, getStoredVolume } from '../components/VolumeControls';
import { 
  FolderOpen, Download, Play, Video, Music, Image as ImageIcon, Film,
  Users, Sun,
  Loader2, RefreshCw, Search, Trash2, Eye, Grid,
  Calendar, Clock, Plus, Filter, ChevronDown, MoreVertical,
  CheckCircle2, Sparkles, Tag, Square, CheckSquare,
  Upload, Shuffle, Share2, Pause, Instagram, Youtube,
  Facebook, Smartphone, LayoutGrid, Copy, Package, Lock,
  Pencil, Columns, Trophy, Volume2, FileText, AlertTriangle, Target,
  Star, Flame, Crosshair, TrendingUp, SlidersHorizontal
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Platform icons mapping
const platformIcons = {
  instagram: Instagram,
  tiktok: Smartphone,
  youtube: Youtube,
  facebook: Facebook
};

// Content type badges
const TypeBadge = ({ type }) => {
  const config = {
    video: { label: 'Video', color: 'bg-purple-500/20 text-purple-400', icon: Video },
    image: { label: 'Image', color: 'bg-blue-500/20 text-blue-400', icon: ImageIcon },
    audio: { label: 'Music', color: 'bg-indigo-500/20 text-indigo-400', icon: Music }
  };
  const { label, color, icon: Icon } = config[type] || config.video;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${color}`}>
      <Icon className="w-2.5 h-2.5" />
      {label}
    </span>
  );
};

// Platform badge
const PlatformBadge = ({ platform }) => {
  if (!platform) return null;
  const Icon = platformIcons[platform] || Smartphone;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-zinc-800/80 text-zinc-400">
      <Icon className="w-2.5 h-2.5" />
      {platform.charAt(0).toUpperCase() + platform.slice(1)}
    </span>
  );
};

// Source badge (generated, uploaded, enhanced, campaign)
const SourceBadge = ({ source, isEnhanced }) => {
  if (isEnhanced) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/20 text-amber-400">
        <Sparkles className="w-2.5 h-2.5" />
        Enhanced
      </span>
    );
  }
  if (source === 'upload') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-500/20 text-green-400">
        <Upload className="w-2.5 h-2.5" />
        Uploaded
      </span>
    );
  }
  if (source === 'campaign') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-orange-500/20 text-orange-400">
        <Sparkles className="w-2.5 h-2.5" />
        Campaign
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary/20 text-primary">
      <Sparkles className="w-2.5 h-2.5" />
      Generated
    </span>
  );
};

// Media Card Component with hover actions

// Compute outcome badges for campaign items
const computeVaultBadges = (items) => {
  const campaignGroups = {};
  items.forEach(item => {
    const campId = item.campaign_meta?.campaign_id;
    if (campId) {
      if (!campaignGroups[campId]) campaignGroups[campId] = [];
      campaignGroups[campId].push(item);
    }
  });

  const badges = {};
  Object.values(campaignGroups).forEach(group => {
    let bestScore = -1, bestHook = -1, bestConv = -1, bestEng = -1;
    let winnerId = null, hookId = null, convId = null, engId = null;

    group.forEach(c => {
      const score = c.campaign_meta?.overall_score || 0;
      const hook = c.scores?.hook_strength || c.campaign_meta?.scores?.hook_strength || 0;
      const conv = c.scores?.conversion_probability || c.campaign_meta?.scores?.conversion_probability || 0;
      const eng = (c.preview_views || 0) + (c.preview_clicks || 0);

      if (score > bestScore) { bestScore = score; winnerId = c.id; }
      if (hook > bestHook) { bestHook = hook; hookId = c.id; }
      if (conv > bestConv) { bestConv = conv; convId = c.id; }
      if (eng > bestEng) { bestEng = eng; engId = c.id; }
    });

    if (winnerId && bestScore > 0) badges[winnerId] = [...(badges[winnerId] || []), { label: 'WINNER', icon: Star, color: 'text-amber-400 bg-amber-500/10 border-amber-500/20' }];
    if (hookId && bestHook > 0) badges[hookId] = [...(badges[hookId] || []), { label: 'Best Hook', icon: Flame, color: 'text-orange-400 bg-orange-500/10 border-orange-500/20' }];
    if (convId && bestConv > 0) badges[convId] = [...(badges[convId] || []), { label: 'High Conversion', icon: Crosshair, color: 'text-green-400 bg-green-500/10 border-green-500/20' }];
    if (engId && bestEng > 0) badges[engId] = [...(badges[engId] || []), { label: 'Top Engagement', icon: TrendingUp, color: 'text-blue-400 bg-blue-500/10 border-blue-500/20' }];
  });
  return badges;
};

const VaultBadgePill = ({ badge }) => {
  const Icon = badge.icon;
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-semibold rounded-full border ${badge.color}`} data-testid={`vault-badge-${badge.label.toLowerCase().replace(/\s/g, '-')}`}>
      <Icon className="w-2.5 h-2.5" />
      {badge.label}
    </span>
  );
};

const MediaCard = ({ 
  media, 
  type, 
  isSelected, 
  isSelecting, 
  onSelect, 
  onPreview, 
  onDownload, 
  onDelete,
  onRemix,
  onShare,
  onRelight,
  vaultBadges,
  isDemoMode,
  onDuplicate,
  onUseInCampaign
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioState, setAudioState] = useState('idle'); // idle | loading | playing | paused | error
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = React.useRef(null);
  const audioRef = React.useRef(null);
  
  const API = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('delphi_token');
  
  // Build streaming URLs with auth token as query param (HTML media elements can't send headers)
  const streamVideoUrl = type === 'video' && media.id && token 
    ? `${API}/api/media/video/${media.id}/stream?token=${encodeURIComponent(token)}` 
    : null;
  const streamAudioUrl = type === 'audio' && media.id && token 
    ? `${API}/api/media/audio/${media.id}/stream?token=${encodeURIComponent(token)}` 
    : null;
  
  // Thumbnail uses presigned URL from backend (images load fine via <img>)
  const thumbnailUrl = media.thumbnail_url || media.image_url || null;
  const primaryUrl = media.primary_url || media.video_url || media.image_url || media.audio_url || media.music_url || media.output_url;
  const hasVideo = type === 'video' && (streamVideoUrl || primaryUrl);
  const hasAudio = type === 'audio' && (streamAudioUrl || primaryUrl);
  const isEnhanced = media.source === 'enhanced' || media.operation || media.enhancement_type;
  
  // Handle video hover preview — load and wait for canplay before playing
  const handleMouseEnter = () => {
    setIsHovered(true);
    if (type === 'video' && videoRef.current && streamVideoUrl) {
      // Set src on hover (lazy load)
      if (!videoRef.current.src || videoRef.current.src === '') {
        videoRef.current.src = streamVideoUrl;
        videoRef.current.load();
      }
      // Wait for video to be ready, then play
      const tryPlay = () => {
        if (videoRef.current && videoRef.current.readyState >= 2) {
          videoRef.current.play()
            .then(() => setIsPlaying(true))
            .catch((err) => {
              console.warn('Video autoplay blocked:', err.name);
              setIsPlaying(false);
            });
        }
      };
      if (videoReady) {
        tryPlay();
      } else {
        videoRef.current.addEventListener('canplay', tryPlay, { once: true });
      }
    }
  };
  
  const handleMouseLeave = () => {
    setIsHovered(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };
  
  const handleCardClick = (e) => {
    if (isSelecting) {
      e.preventDefault();
      onSelect(media, type);
    } else if (type === 'audio') {
      // Toggle audio play on click — use stored volume preference
      if (audioRef.current) {
        if (audioPlaying) {
          audioRef.current.pause();
          setAudioPlaying(false);
          setAudioState('paused');
        } else {
          setAudioState('loading');
          const vol = getStoredVolume('audio');
          audioRef.current.volume = vol;
          audioRef.current.muted = false;
          audioRef.current.play()
            .then(() => {
              setAudioPlaying(true);
              setAudioState('playing');
            })
            .catch((err) => {
              console.error('Audio play failed:', err.name, err.message, 
                'readyState:', audioRef.current?.readyState, 
                'src:', audioRef.current?.currentSrc?.substring(0, 60));
              setAudioState('error');
              setAudioPlaying(false);
            });
        }
      }
    } else if (type === 'video' && !isPlaying) {
      // On mobile/tap: open preview modal
      onPreview(media, type);
    }
  };

  const imageDisplayUrl = type === 'image' ? (media.image_url || thumbnailUrl) : thumbnailUrl;

  return (
    <div 
      className={`group relative overflow-hidden rounded-xl bg-zinc-900/50 border transition-all duration-200 cursor-pointer ${
        isSelected 
          ? 'border-primary ring-2 ring-primary/30' 
          : 'border-zinc-800/50 hover:border-zinc-700'
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleCardClick}
      data-testid={`media-card-${media.id}`}
    >
      {/* Selection checkbox */}
      {isSelecting && (
        <div className="absolute top-2 left-2 z-20">
          <button
            onClick={(e) => { e.stopPropagation(); onSelect(media, type); }}
            className={`w-6 h-6 rounded-lg flex items-center justify-center transition-all ${
              isSelected 
                ? 'bg-primary text-white' 
                : 'bg-zinc-800/80 text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            {isSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
          </button>
        </div>
      )}
      
      {/* Thumbnail / Preview Area — MEDIA FIRST */}
      <div className="aspect-[4/5] relative overflow-hidden bg-zinc-800">
        
        {/* ═══ IMAGE CONTENT ═══ */}
        {type === 'image' && imageDisplayUrl && !imgError && (
          <img 
            src={imageDisplayUrl} 
            alt={media.title || media.prompt || 'Image'}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
            onError={() => setImgError(true)}
          />
        )}
        {/* Image placeholder */}
        {type === 'image' && (!imageDisplayUrl || imgError) && (
          <div className="w-full h-full flex flex-col items-center justify-center bg-zinc-800/80">
            <ImageIcon className="w-10 h-10 text-zinc-600 mb-2" />
            <span className="text-[10px] text-zinc-600 font-medium">Image</span>
          </div>
        )}
        
        {/* ═══ VIDEO CONTENT ═══ */}
        {type === 'video' && hasVideo && (
          <>
            {/* Video thumbnail (shows when not playing) */}
            {!isPlaying && thumbnailUrl && !imgError && (
              <img 
                src={thumbnailUrl} 
                alt={media.title || media.topic || 'Video'}
                className="w-full h-full object-cover absolute inset-0"
                loading="lazy"
                onError={() => setImgError(true)}
              />
            )}
            {/* Video element for hover preview — uses streaming proxy */}
            <video
              ref={videoRef}
              className={`w-full h-full object-cover ${isPlaying ? 'opacity-100' : 'opacity-0'}`}
              muted
              playsInline
              preload="none"
              loop
              onCanPlay={() => setVideoReady(true)}
              onError={(e) => {
                console.warn('Video load error:', media.id, e.target.error?.message);
                setVideoReady(false);
              }}
            />
            {/* Play overlay when not playing */}
            {!isPlaying && (
              <div className="absolute inset-0 flex items-center justify-center">
                {(!thumbnailUrl || imgError) && (
                  <div className="absolute inset-0 bg-zinc-800/80 flex flex-col items-center justify-center">
                    <Film className="w-10 h-10 text-zinc-600 mb-2" />
                    <span className="text-[10px] text-zinc-600 font-medium">Video</span>
                  </div>
                )}
                <div className="w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center z-10">
                  <Play className="w-5 h-5 text-white ml-0.5" />
                </div>
              </div>
            )}
          </>
        )}
        {/* Video with no source placeholder */}
        {type === 'video' && !hasVideo && (
          <div className="w-full h-full flex flex-col items-center justify-center bg-zinc-800/80">
            <Film className="w-10 h-10 text-zinc-600 mb-2" />
            <span className="text-[10px] text-zinc-600 font-medium">Video unavailable</span>
          </div>
        )}
        
        {/* ═══ AUDIO CONTENT ═══ */}
        {type === 'audio' && (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-indigo-500/10 via-purple-500/5 to-pink-500/10 relative">
            {/* Waveform visualization */}
            <div className="flex items-end gap-[3px] h-16 mb-4">
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className={`w-1.5 rounded-full transition-all duration-300 ${
                    audioPlaying ? 'bg-primary animate-pulse' : 'bg-zinc-600'
                  }`}
                  style={{
                    height: `${20 + Math.sin(i * 0.7) * 25 + Math.random() * 15}%`,
                    animationDelay: `${i * 50}ms`
                  }}
                />
              ))}
            </div>
            <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
              audioState === 'error' ? 'bg-red-500/30' :
              audioState === 'loading' ? 'bg-yellow-500/30 animate-pulse' :
              audioPlaying ? 'bg-primary scale-110' : 'bg-zinc-700/80 group-hover:bg-primary/80'
            }`}>
              {audioState === 'loading' ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : audioState === 'error' ? (
                <span className="text-xs text-red-300">!</span>
              ) : audioPlaying ? (
                <Pause className="w-6 h-6 text-white" />
              ) : (
                <Play className="w-6 h-6 text-white ml-0.5" />
              )}
            </div>
            <span className="text-xs text-zinc-500 mt-2">
              {audioState === 'error' ? 'Playback failed — tap to retry' :
               media.genre ? media.genre.charAt(0).toUpperCase() + media.genre.slice(1) : 'Audio Track'}
              {media.duration ? ` · ${media.duration}s` : ''}
            </span>
            {/* Inline volume control when playing */}
            {audioPlaying && (
              <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                <VolumeControl mediaRef={audioRef} type="audio" compact />
              </div>
            )}
            {streamAudioUrl && (
              <audio
                ref={audioRef}
                src={streamAudioUrl}
                preload="none"
                onEnded={() => { setAudioPlaying(false); setAudioState('idle'); }}
                onError={(e) => {
                  console.error('Audio error:', media.id, e.target.error?.message);
                  setAudioState('error');
                  setAudioPlaying(false);
                }}
              />
            )}
          </div>
        )}
        
        {/* Badges overlay (top-right) */}
        <div className="absolute top-2 right-2 z-10 flex flex-col gap-1 items-end">
          <TypeBadge type={type} />
          {media.platform && <PlatformBadge platform={media.platform} />}
        </div>
        
        {/* Source badge (bottom left) */}
        <div className="absolute bottom-2 left-2 z-10">
          <SourceBadge source={media.source} isEnhanced={isEnhanced} />
        </div>
        
        {/* Hover Actions Overlay */}
        <div className={`absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent transition-opacity duration-200 ${
          isHovered && !isSelecting ? 'opacity-100' : 'opacity-0'
        }`}>
          <div className="absolute bottom-0 left-0 right-0 p-3">
            {/* Quick action icons */}
            <div className="flex justify-center gap-2 mb-2">
              <button
                onClick={(e) => { e.stopPropagation(); onPreview(media, type); }}
                className="w-9 h-9 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-all"
                title="Play / Preview"
                data-testid={`preview-btn-${media.id}`}
              >
                <Eye className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onDownload(media); }}
                className="w-9 h-9 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-all"
                title="Download"
                data-testid={`download-btn-${media.id}`}
              >
                <Download className="w-4 h-4" />
              </button>
              {!isDemoMode && (
                <button
                  onClick={(e) => { e.stopPropagation(); onShare?.(media); }}
                  className="w-9 h-9 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-all"
                  title="Copy public share link"
                  data-testid={`share-btn-${media.id}`}
                >
                  <Share2 className="w-4 h-4" />
                </button>
              )}
              {!isDemoMode && (
                <button
                  onClick={(e) => { e.stopPropagation(); onDuplicate?.(media, type); }}
                  className="w-9 h-9 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-all"
                  title="Duplicate"
                  data-testid={`duplicate-btn-${media.id}`}
                >
                  <Copy className="w-4 h-4" />
                </button>
              )}
              {type === 'image' && !isDemoMode && primaryUrl && (
                <button
                  onClick={(e) => { e.stopPropagation(); onRelight?.(media); }}
                  className="w-9 h-9 rounded-lg bg-orange-500/20 backdrop-blur-sm flex items-center justify-center text-orange-300 hover:bg-orange-500/35 transition-all"
                  title="Relight this image"
                  data-testid={`relight-btn-${media.id}`}
                >
                  <Sun className="w-4 h-4" />
                </button>
              )}
              {media.campaign_meta && (
                <button
                  onClick={(e) => { e.stopPropagation(); onUseInCampaign?.(media); }}
                  className="w-9 h-9 rounded-lg bg-orange-500/20 backdrop-blur-sm flex items-center justify-center text-orange-400 hover:bg-orange-500/30 transition-all"
                  title="Use in new campaign"
                  data-testid={`use-in-campaign-btn-${media.id}`}
                >
                  <Target className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(media, type); }}
                className={`w-9 h-9 rounded-lg bg-red-500/20 backdrop-blur-sm flex items-center justify-center text-red-400 hover:bg-red-500/30 transition-all ${isDemoMode ? 'hidden' : ''}`}
                title="Delete"
                data-testid={`delete-btn-${media.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Card Footer - Title & Meta (SECONDARY) */}
      <div className="p-3">
        <p className="text-sm font-medium text-white truncate" data-testid={`media-title-${media.id}`}>
          {media.title || media.topic || media.prompt || media.name || media.filename || 'Untitled'}
        </p>
        <div className="flex items-center gap-2 mt-1 text-xs text-zinc-500">
          <span>{new Date(media.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
          {media.influencer_name && (
            <>
              <span>·</span>
              <span className="truncate">{media.influencer_name}</span>
            </>
          )}
          {media.campaign_meta && (
            <>
              <span>·</span>
              <span className="text-orange-400 font-medium">Score {media.campaign_meta.overall_score}</span>
              <span>·</span>
              <span className="truncate">{media.campaign_meta.framework}</span>
            </>
          )}
        </div>
        {/* Outcome Badges for campaign items */}
        {vaultBadges?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {vaultBadges.map((b) => <VaultBadgePill key={b.label} badge={b} />)}
          </div>
        )}
        {/* Tags */}
        {media.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {media.tags.slice(0, 2).map(tag => (
              <span key={tag} className="px-1.5 py-0.5 text-[10px] bg-zinc-800 text-zinc-400 rounded">
                {tag}
              </span>
            ))}
            {media.tags.length > 2 && (
              <span className="px-1.5 py-0.5 text-[10px] bg-zinc-800 text-zinc-500 rounded">
                +{media.tags.length - 2}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Preview Modal Component
const PreviewModal = ({ open, onClose, media, type, onDownload, onRemix, onShare, onEdit }) => {
  const { token, user } = useAuth();
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const videoRef = React.useRef(null);
  const audioRef = React.useRef(null);
  
  if (!media) return null;
  
  const API = process.env.REACT_APP_BACKEND_URL;
  
  const videoStreamUrl = type === 'video' && media.id && token 
    ? `${API}/api/media/video/${media.id}/stream?token=${encodeURIComponent(token)}` 
    : null;
  const audioStreamUrl = type === 'audio' && media.id && token 
    ? `${API}/api/media/audio/${media.id}/stream?token=${encodeURIComponent(token)}` 
    : null;
  const imageUrl = media.primary_url || media.image_url || media.thumbnail_url;
  
  const toggleAudio = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      setAudioError(false);
      const vol = getStoredVolume('audio');
      audioRef.current.volume = vol;
      audioRef.current.muted = false;
      audioRef.current.play()
        .then(() => setIsPlaying(true))
        .catch((err) => {
          console.error('Preview audio error:', err.name, err.message);
          setAudioError(true);
        });
    }
  };
  
  const title = media.title || media.topic || media.name || media.prompt || 'Untitled';
  const caption = media.script || media.caption || media.prompt || '';
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl bg-zinc-900 border-zinc-800 p-0 overflow-hidden" aria-describedby="preview-description">
        <span id="preview-description" className="sr-only">Preview of {type} content</span>
        <div className="flex flex-col lg:flex-row max-h-[90vh]">
          
          {/* ═══ MEDIA DISPLAY PANEL ═══ */}
          <div className="flex-1 bg-black flex items-center justify-center relative overflow-hidden">
            
            {/* VIDEO */}
            {type === 'video' && videoStreamUrl && (
              <div className="w-full h-full flex items-center justify-center p-4 relative" style={{ maxHeight: '80vh' }}>
                <VideoMixPlayer
                  videoId={media.id}
                  videoSrc={videoStreamUrl}
                  token={token}
                  userTier={user?.tier}
                  isFounder={user?.is_founder}
                />
              </div>
            )}
            
            {/* IMAGE */}
            {type === 'image' && imageUrl && (
              <div className="w-full h-full flex items-center justify-center p-4">
                <img 
                  src={imageUrl} 
                  alt={title}
                  className="max-w-full max-h-[76vh] object-contain rounded-lg"
                  data-testid="preview-image"
                />
              </div>
            )}
            
            {/* AUDIO */}
            {type === 'audio' && (
              <div className="flex flex-col items-center justify-center p-8 w-full">
                <div className="w-32 h-32 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center mb-6">
                  <Music className="w-16 h-16 text-indigo-400" />
                </div>
                <button
                  onClick={toggleAudio}
                  className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 hover:scale-105 transition-transform ${
                    audioError ? 'bg-red-500' : isPlaying ? 'bg-primary' : 'bg-zinc-700 hover:bg-primary'
                  }`}
                  data-testid="preview-audio-play"
                >
                  {audioError ? (
                    <span className="text-white text-xs font-medium">Retry</span>
                  ) : isPlaying ? (
                    <Pause className="w-7 h-7 text-white" />
                  ) : (
                    <Play className="w-7 h-7 text-white ml-0.5" />
                  )}
                </button>
                {audioStreamUrl && (
                  <audio 
                    ref={audioRef} 
                    src={audioStreamUrl} 
                    preload="none"
                    onEnded={() => setIsPlaying(false)}
                    onError={(e) => {
                      console.error('Preview audio error:', e.target.error?.code, e.target.error?.message);
                      setAudioError(true);
                    }}
                  />
                )}
                {/* Volume control for audio */}
                <div className="mt-3 mb-3">
                  <VolumeControl mediaRef={audioRef} type="audio" />
                </div>
                <p className="text-white font-medium text-center">{title}</p>
                {media.genre && <p className="text-xs text-zinc-500 mt-1">{media.genre}{media.duration ? ` · ${media.duration}s` : ''}</p>}
                {audioError && <p className="text-xs text-red-400 mt-2">Could not load audio — tap Retry</p>}
              </div>
            )}
          </div>
          
          {/* ═══ INFO PANEL ═══ */}
          <div className="w-full lg:w-80 flex flex-col border-t lg:border-t-0 lg:border-l border-zinc-800 max-h-[90vh] lg:max-h-none">
            
            {/* Fixed header */}
            <div className="p-5 pb-3 shrink-0">
              <DialogHeader>
                <DialogTitle className="text-lg text-white leading-snug line-clamp-2" data-testid="preview-title">
                  {title}
                </DialogTitle>
              </DialogHeader>
            </div>
            
            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto px-5 pb-3">
              {/* Meta */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-zinc-400">
                  <Calendar className="w-4 h-4 shrink-0" />
                  <span>{new Date(media.created_at).toLocaleDateString('en-US', { 
                    month: 'long', day: 'numeric', year: 'numeric' 
                  })}</span>
                </div>
                {media.influencer_name && (
                  <div className="flex items-center gap-2 text-sm text-zinc-400">
                    <Users className="w-4 h-4 shrink-0" />
                    <span className="truncate">{media.influencer_name}</span>
                  </div>
                )}
                {media.platform && (
                  <div className="flex items-center gap-2 text-sm text-zinc-400">
                    {React.createElement(platformIcons[media.platform] || Smartphone, { className: "w-4 h-4 shrink-0" })}
                    <span>{media.platform.charAt(0).toUpperCase() + media.platform.slice(1)}</span>
                  </div>
                )}
              </div>
              
              {/* Caption/Script */}
              {caption && caption !== title && (
                <div className="mb-4">
                  <h4 className="text-xs font-medium text-zinc-500 uppercase mb-1.5">
                    {type === 'audio' ? 'Prompt' : 'Script'}
                  </h4>
                  <p className="text-sm text-zinc-300 bg-zinc-800/50 p-3 rounded-lg max-h-32 overflow-y-auto leading-relaxed">
                    {caption}
                  </p>
                </div>
              )}
              
              {/* Tags */}
              {media.tags?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-medium text-zinc-500 uppercase mb-1.5">Tags</h4>
                  <div className="flex flex-wrap gap-1">
                    {media.tags.map(tag => (
                      <span key={tag} className="px-2 py-1 text-xs bg-zinc-800 text-zinc-400 rounded-lg">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Fixed footer — Action Buttons */}
            <div className="p-5 pt-3 border-t border-zinc-800/50 shrink-0 space-y-2">
              {/* Primary: Edit */}
              <Button 
                className="w-full bg-primary hover:bg-primary/90"
                onClick={() => onEdit?.(media, type)}
                data-testid="preview-edit-btn"
              >
                <Pencil className="w-4 h-4 mr-2" />
                Edit / Enhance
              </Button>

              {/* Export & Publish panel */}
              <ExportPublishPanel
                title={title}
                description={caption}
                hashtags={media.hashtags || media.tags}
                downloadUrl={null}
              />
              
              {/* Download + Remix */}
              <div className="grid grid-cols-2 gap-2">
                <Button 
                  variant="outline" 
                  className="border-zinc-700 hover:bg-zinc-800"
                  onClick={() => onDownload(media)}
                  data-testid="preview-download-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
                <Button 
                  variant="outline" 
                  className="border-zinc-700 hover:bg-zinc-800"
                  onClick={() => onRemix?.(media, type)}
                  data-testid="preview-remix-btn"
                >
                  <Shuffle className="w-4 h-4 mr-2" />
                  Remix
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Empty State Component
const EmptyState = ({ filter }) => (
  <div className="text-center py-16 px-4">
    <div className="w-20 h-20 rounded-2xl bg-zinc-800/50 flex items-center justify-center mx-auto mb-6">
      <FolderOpen className="w-10 h-10 text-zinc-600" />
    </div>
    <h3 className="text-xl font-semibold text-white mb-2">
      {filter === 'all' ? 'Your creations will appear here' : `No ${filter} content yet`}
    </h3>
    <p className="text-zinc-500 mb-6 max-w-md mx-auto">
      Create amazing content with AI and it will show up in your library for easy access.
    </p>
    <div className="flex flex-wrap gap-3 justify-center">
      <Link to="/studio/videos">
        <Button className="btn-oracle">
          <Video className="w-4 h-4 mr-2" />
          Create Video
        </Button>
      </Link>
      <Link to="/studio/images">
        <Button variant="outline" className="border-zinc-700 hover:bg-zinc-800">
          <ImageIcon className="w-4 h-4 mr-2" />
          Create Image
        </Button>
      </Link>
      <Link to="/studio/music">
        <Button variant="outline" className="border-zinc-700 hover:bg-zinc-800">
          <Music className="w-4 h-4 mr-2" />
          Create Music
        </Button>
      </Link>
    </div>
  </div>
);

// Campaign Compare Modal
const CampaignCompareModal = ({ open, onClose, items }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  if (!open || !items || items.length < 2) return null;

  const creatives = items.filter(s => s.media?.source === 'campaign').map(s => s.media);
  if (creatives.length < 2) return null;

  const maxScore = Math.max(...creatives.map(c => c.campaign_meta?.overall_score || 0));
  const SCORE_DIMS = [
    { key: 'hook_strength', label: 'Hook', color: '#f97316' },
    { key: 'emotional_trigger', label: 'Emotion', color: '#ec4899' },
    { key: 'clarity', label: 'Clarity', color: '#3b82f6' },
    { key: 'platform_optimization', label: 'Platform', color: '#8b5cf6' },
    { key: 'conversion_probability', label: 'Conversion', color: '#22c55e' },
  ];

  // Compute badges for compared creatives
  const compareBadges = {};
  let bestHook = -1, bestConv = -1, bestEng = -1;
  let hookId = null, convId = null, engId = null;
  creatives.forEach(c => {
    const hook = c.scores?.hook_strength || c.campaign_meta?.scores?.hook_strength || 0;
    const conv = c.scores?.conversion_probability || c.campaign_meta?.scores?.conversion_probability || 0;
    const eng = (c.preview_views || 0) + (c.preview_clicks || 0);
    if (hook > bestHook) { bestHook = hook; hookId = c.id; }
    if (conv > bestConv) { bestConv = conv; convId = c.id; }
    if (eng > bestEng) { bestEng = eng; engId = c.id; }
  });
  if (hookId && bestHook > 0) compareBadges[hookId] = [...(compareBadges[hookId] || []), { label: 'Best Hook', icon: Flame, color: 'text-orange-400 bg-orange-500/10 border-orange-500/20' }];
  if (convId && bestConv > 0) compareBadges[convId] = [...(compareBadges[convId] || []), { label: 'High Conversion', icon: Crosshair, color: 'text-green-400 bg-green-500/10 border-green-500/20' }];
  if (engId && bestEng > 0) compareBadges[engId] = [...(compareBadges[engId] || []), { label: 'Top Engagement', icon: TrendingUp, color: 'text-blue-400 bg-blue-500/10 border-blue-500/20' }];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="compare-modal">
        <DialogHeader>
          <DialogTitle className="text-xl text-zinc-100 flex items-center gap-2">
            <Columns className="w-5 h-5 text-orange-500" /> Campaign Creative Comparison
          </DialogTitle>
        </DialogHeader>

        <div className={`grid gap-4 mt-4`} style={{ gridTemplateColumns: `repeat(${Math.min(creatives.length, 3)}, 1fr)` }}>
          {creatives.slice(0, 3).map((c, idx) => {
            const meta = c.campaign_meta || {};
            const score = meta.overall_score || 0;
            const isWinner = score === maxScore && score > 0;
            const scores = c.scores || {};

            return (
              <div key={c.id} className={`relative rounded-2xl border p-4 ${isWinner ? 'border-orange-500/60 bg-orange-500/5' : 'border-zinc-800 bg-zinc-900/60'}`} data-testid={`compare-col-${idx}`}>
                {isWinner && (
                  <div className="absolute -top-2.5 left-3 flex items-center gap-1 px-2 py-0.5 bg-orange-500 text-white text-[10px] font-bold rounded-full">
                    <Trophy className="w-2.5 h-2.5" /> WINNER
                  </div>
                )}

                {/* Letter + Framework + Score */}
                <div className="flex items-center justify-between mb-3 pt-1">
                  <div>
                    <span className="text-lg font-bold text-zinc-100">Creative {meta.letter || String.fromCharCode(65 + idx)}</span>
                    <div className="text-[10px] text-zinc-500 bg-zinc-800 px-1.5 py-0.5 rounded mt-0.5 inline-block">{meta.framework || 'N/A'}</div>
                    {/* Outcome Badges */}
                    {compareBadges[c.id]?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {compareBadges[c.id].map((b) => <VaultBadgePill key={b.label} badge={b} />)}
                      </div>
                    )}
                  </div>
                  <div className="text-center">
                    <div className={`text-2xl font-black ${score >= 80 ? 'text-green-400' : score >= 60 ? 'text-amber-400' : 'text-red-400'}`}>{score}</div>
                    <div className="text-[9px] text-zinc-600 uppercase tracking-wider">Score</div>
                  </div>
                </div>

                {/* Hook */}
                <div className="mb-3 p-2.5 bg-zinc-800/50 rounded-lg border-l-2 border-orange-500">
                  <p className="text-[10px] text-zinc-500 font-medium mb-0.5">Hook</p>
                  <p className="text-xs text-zinc-200 leading-relaxed">{meta.hook || c.title || 'N/A'}</p>
                </div>

                {/* Score Breakdown */}
                <div className="space-y-1.5 mb-3">
                  {SCORE_DIMS.map(dim => (
                    <div key={dim.key} className="space-y-0.5">
                      <div className="flex justify-between text-[10px]">
                        <span className="text-zinc-500">{dim.label}</span>
                        <span className="text-zinc-400 font-medium">{scores[dim.key] || 0}</span>
                      </div>
                      <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${scores[dim.key] || 0}%`, background: dim.color }} />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Caption */}
                {meta.caption && (
                  <div className="mb-3 p-2 bg-zinc-800/30 rounded-lg">
                    <p className="text-[10px] text-zinc-500 font-medium mb-0.5 flex items-center gap-1"><FileText className="w-2.5 h-2.5" /> Caption</p>
                    <p className="text-[11px] text-zinc-400 line-clamp-3">{meta.caption}</p>
                  </div>
                )}

                {/* Render Status */}
                <div className="mb-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                    c.video_status === 'ready' ? 'bg-green-500/10 text-green-400' :
                    c.video_status === 'rendering' ? 'bg-blue-500/10 text-blue-400' :
                    c.video_status === 'failed' ? 'bg-red-500/10 text-red-400' :
                    'bg-zinc-800 text-zinc-500'
                  }`}>
                    {c.video_status === 'ready' ? <><CheckCircle2 className="w-2.5 h-2.5" /> Video Ready</> :
                     c.video_status === 'rendering' ? <><Loader2 className="w-2.5 h-2.5 animate-spin" /> Rendering</> :
                     c.video_status === 'failed' ? <><AlertTriangle className="w-2.5 h-2.5" /> Failed</> :
                     <><Clock className="w-2.5 h-2.5" /> No Video</>}
                  </span>
                </div>

                {/* Audio Players */}
                <div className="space-y-2">
                  {c.audio_url && (
                    <div className="p-2 bg-zinc-800/30 rounded-lg">
                      <p className="text-[10px] text-zinc-500 mb-1 flex items-center gap-1"><Volume2 className="w-2.5 h-2.5" /> Voice</p>
                      <audio controls src={`${BACKEND_URL}${c.audio_url}`} className="w-full h-7" preload="none" data-testid={`compare-voice-${idx}`} />
                    </div>
                  )}
                  {c.music_url && (
                    <div className="p-2 bg-zinc-800/30 rounded-lg">
                      <p className="text-[10px] text-zinc-500 mb-1 flex items-center gap-1"><Music className="w-2.5 h-2.5" /> Music</p>
                      <audio controls src={`${BACKEND_URL}${c.music_url}`} className="w-full h-7" preload="none" data-testid={`compare-music-${idx}`} />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const LibraryPage = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isDemoMode = user?.demo_mode && user?.is_founder;
  
  // Library state
  const [videos, setVideos] = useState([]);
  const [images, setImages] = useState([]);
  const [audio, setAudio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filter state
  const [activeFilter, setActiveFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // Advanced filters for campaign creatives
  const [frameworkFilter, setFrameworkFilter] = useState('all');
  const [scoreMinFilter, setScoreMinFilter] = useState(0);
  const [renderStatusFilter, setRenderStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // Multi-select state
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showBatchTagModal, setShowBatchTagModal] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  
  // Preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewMedia, setPreviewMedia] = useState(null);
  const [previewType, setPreviewType] = useState('video');

  // Relight quick-action modal state
  const [relightOpen, setRelightOpen] = useState(false);
  const [relightMedia, setRelightMedia] = useState(null);
  const [relightPresets, setRelightPresets] = useState([]);
  const [relightActive, setRelightActive] = useState('golden_hour');
  const [relightRunning, setRelightRunning] = useState(false);
  const [relightResult, setRelightResult] = useState(null);

  const openRelight = useCallback(async (media) => {
    setRelightMedia(media);
    setRelightResult(null);
    setRelightOpen(true);
    if (relightPresets.length === 0) {
      try {
        const r = await axios.get(`${API}/relight/presets`);
        setRelightPresets(r.data?.lighting_presets || []);
      } catch { /* ignore */ }
    }
  }, [relightPresets.length]);

  // Fetch all library content
  const fetchLibrary = useCallback(async (silent = false) => {
    if (!token) return;
    
    if (!silent) setLoading(true);
    if (silent) setRefreshing(true);
    
    try {
      if (isDemoMode) {
        // In demo mode, load curated demo campaign creatives as library items
        const demoResp = await axios.get(`${API}/demo/campaigns`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const demoItems = [];
        (demoResp.data || []).forEach(camp => {
          (camp.creatives || []).forEach(c => {
            demoItems.push({
              id: c.id,
              type: 'video',
              status: 'ready',
              source: 'campaign',
              topic: c.hook,
              prompt: c.hook,
              created_at: camp.created_at,
              thumbnail_url: null,
              primary_url: null,
              campaign_meta: {
                campaign_id: camp.id,
                campaign_name: camp.product,
                letter: c.letter,
                framework: c.framework_label,
                overall_score: c.overall_score,
                scores: c.scores,
              },
              video_status: c.video_status,
              scores: c.scores,
              is_demo: true,
            });
          });
        });
        setVideos(demoItems);
        setImages([]);
        setAudio([]);
      } else {
        const params = new URLSearchParams();
        if (selectedTags.length > 0) {
          params.append('tags', selectedTags.join(','));
        }
        
        const response = await axios.get(`${API}/library/?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const data = response.data;
        const items = data.items || [];
        
        // Filter ready items that have usable media
        const readyItems = items.filter(item => 
          item.status === 'ready' && !item.hidden &&
          (item.primary_url || item.thumbnail_url || item.image_url || item.video_url || item.audio_url || item.music_url || item.output_url)
        );
        
        // Split by type
        setVideos(readyItems.filter(i => i.type === 'video'));
        setImages(readyItems.filter(i => i.type === 'image'));
        setAudio(readyItems.filter(i => i.type === 'audio'));
      }
    } catch (error) {
      console.error('Library fetch error:', error);
      if (!silent) toast.error('Failed to load library');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, selectedTags, isDemoMode]);

  const runRelight = useCallback(async () => {
    if (!relightMedia) return;
    const sourceUrl = relightMedia.primary_url || relightMedia.image_url || relightMedia.output_url;
    if (!sourceUrl) { toast.error('No source image URL available'); return; }
    setRelightRunning(true);
    setRelightResult(null);
    try {
      const fd = new FormData();
      fd.append('source_url', sourceUrl);
      fd.append('preset_id', relightActive);
      const { data } = await axios.post(`${API}/relight/start`, fd, {
        headers: { Authorization: `Bearer ${localStorage.getItem('delphi_token')}`, 'Content-Type': 'multipart/form-data' },
      });
      setRelightResult(data);
      toast.success('Relit image ready');
      fetchLibrary(true);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Relight failed';
      toast.error(msg);
    } finally {
      setRelightRunning(false);
    }
  }, [relightMedia, relightActive, fetchLibrary]);


  useEffect(() => {
    fetchLibrary();
    const interval = setInterval(() => fetchLibrary(true), 30000);
    return () => clearInterval(interval);
  }, [fetchLibrary]);

  // Delete handler with optimistic UI
  const handleDelete = async (media, type) => {
    if (!window.confirm('Delete this item permanently?')) return;

    // Optimistic UI update
    if (type === 'video') {
      setVideos(prev => prev.filter(v => v.id !== media.id));
    } else if (type === 'image') {
      setImages(prev => prev.filter(i => i.id !== media.id));
    } else if (type === 'audio') {
      setAudio(prev => prev.filter(a => a.id !== media.id));
    }

    try {
      let endpoint = '';
      const source = media.source || 'generated';
      const isEnhanced = media.operation || media.enhancement_type;
      
      if (type === 'video') {
        if (source === 'upload') {
          endpoint = `${API}/uploads/video/${media.id}`;
        } else if (isEnhanced) {
          endpoint = `${API}/videos/enhanced/${media.id}`;
        } else {
          endpoint = `${API}/reels/${media.id}`;
        }
      } else if (type === 'image') {
        if (source === 'upload') {
          endpoint = `${API}/uploads/image/${media.id}`;
        } else if (isEnhanced) {
          endpoint = `${API}/images/enhanced/${media.id}`;
        } else {
          endpoint = `${API}/images/${media.id}`;
        }
      } else if (type === 'audio') {
        if (source === 'upload') {
          endpoint = `${API}/uploads/audio/${media.id}`;
        } else {
          endpoint = `${API}/music/${media.id}`;
        }
      }

      await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Deleted successfully');
    } catch (error) {
      console.error('Delete error:', error);
      fetchLibrary(true);
      toast.error('Failed to delete. Please try again.');
    }
  };

  // Download handler
  const handleDownload = (media) => {
    const API = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('delphi_token');
    let url;
    // Use streaming proxy for video/audio downloads
    if (media.type === 'video' && media.id && token) {
      url = `${API}/api/media/video/${media.id}/stream?token=${encodeURIComponent(token)}`;
    } else if (media.type === 'audio' && media.id && token) {
      url = `${API}/api/media/audio/${media.id}/stream?token=${encodeURIComponent(token)}`;
    } else {
      url = media.primary_url || media.video_url || media.image_url || media.audio_url || 
            media.music_url || media.output_url || media.output_video;
    }
    if (url) {
      window.open(url, '_blank');
      toast.success('Download started');
    }
  };

  // Preview handler
  const openPreview = (media, type) => {
    setPreviewMedia(media);
    setPreviewType(type);
    setPreviewOpen(true);
  };

  // Selection handlers
  const toggleSelection = (media, type) => {
    const key = `${media.id}-${type}`;
    setSelectedItems(prev => {
      const exists = prev.find(s => `${s.id}-${s.type}` === key);
      if (exists) {
        return prev.filter(s => `${s.id}-${s.type}` !== key);
      }
      return [...prev, { id: media.id, type, media }];
    });
  };

  const isSelected = (media, type) => {
    return selectedItems.some(s => s.id === media.id && s.type === type);
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;
    if (!window.confirm(`Delete ${selectedItems.length} items permanently?`)) return;

    for (const item of selectedItems) {
      await handleDelete(item.media, item.type);
    }
    setSelectedItems([]);
    setIsSelecting(false);
  };

  // Bulk download
  const handleBulkDownload = () => {
    selectedItems.forEach(item => handleDownload(item.media));
  };

  // Remix handler
  const handleRemix = (media, type) => {
    if (type === 'video') {
      navigate('/studio/videos', { state: { remix: media } });
    } else if (type === 'image') {
      navigate('/studio/images', { state: { remix: media } });
    }
    toast.info('Remix mode activated');
  };

  // Edit/Enhance handler — routes to the correct studio with source preloaded
  const handleEdit = (media, type) => {
    if (type === 'image') {
      navigate('/studio/images', { state: { edit: media, source_image: media.primary_url || media.image_url } });
    } else if (type === 'video') {
      navigate('/studio/videos', { state: { edit: media, source_video: media.primary_url || media.video_url } });
    } else if (type === 'audio') {
      navigate('/studio/music', { state: { edit: media, source_audio: media.primary_url || media.audio_url } });
    }
    toast.info('Edit mode — your content is loaded');
  };

  // Share handler
  const handleShare = async (media) => {
    try {
      toast.loading('Creating public link...', { id: 'share' });
      const { data } = await axios.post(`${API}/shares`, {
        resource_type: 'media',
        resource_id: media.id,
      }, { headers: { Authorization: `Bearer ${token}` } });
      const publicUrl = `${window.location.origin}${data.share_url || data.path}`;
      if (navigator.share) {
        toast.dismiss('share');
        try {
          await navigator.share({ url: publicUrl, title: media.title || media.topic || 'Check out my creation on Delphi AI' });
          return;
        } catch {
          // User cancelled native share — fall through to clipboard
        }
      }
      try {
        await navigator.clipboard.writeText(publicUrl);
        toast.success('Public link copied to clipboard', { id: 'share' });
      } catch {
        toast.success(`Link created: ${publicUrl}`, { id: 'share', duration: 8000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create share link', { id: 'share' });
    }
  };

  const handleDuplicate = async (media, type) => {
    try {
      toast.loading('Duplicating...', { id: 'dup' });
      await axios.post(`${API}/library/${media.id}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Duplicate created', { id: 'dup' });
      fetchLibrary(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Duplicate failed', { id: 'dup' });
    }
  };

  const handleUseInCampaign = (media) => {
    const meta = media.campaign_meta || {};
    const params = new URLSearchParams();
    if (meta.campaign_name) params.set('product', meta.campaign_name);
    if (media.platform) params.set('platform', media.platform);
    if (media.tone) params.set('tone', media.tone);
    navigate(`/campaigns?${params.toString()}`);
    toast.info('Campaign generator pre-filled from creative');
  };

  // Filter and search logic
  const filteredContent = useMemo(() => {
    let items = [];
    
    if (activeFilter === 'all' || activeFilter === 'videos') {
      items = [...items, ...videos];
    }
    if (activeFilter === 'all' || activeFilter === 'images') {
      items = [...items, ...images];
    }
    if (activeFilter === 'all' || activeFilter === 'audio') {
      items = [...items, ...audio];
    }
    
    // Source filter
    if (sourceFilter !== 'all') {
      if (sourceFilter === 'enhanced') {
        items = items.filter(i => i.operation || i.enhancement_type);
      } else if (sourceFilter === 'campaign') {
        items = items.filter(i => i.source === 'campaign');
      } else {
        items = items.filter(i => i.source === sourceFilter && !i.operation);
      }
    }
    
    // Framework filter
    if (frameworkFilter !== 'all') {
      items = items.filter(i => i.campaign_meta?.framework === frameworkFilter);
    }

    // Score min filter
    if (scoreMinFilter > 0) {
      items = items.filter(i => (i.campaign_meta?.overall_score || 0) >= scoreMinFilter);
    }

    // Render status filter
    if (renderStatusFilter !== 'all') {
      items = items.filter(i => (i.video_status || 'pending') === renderStatusFilter);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      let cutoff;
      if (dateFilter === 'today') cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      else if (dateFilter === 'week') cutoff = new Date(now - 7 * 86400000);
      else if (dateFilter === 'month') cutoff = new Date(now - 30 * 86400000);
      if (cutoff) items = items.filter(i => new Date(i.created_at) >= cutoff);
    }

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      items = items.filter(i => 
        (i.topic || i.prompt || i.name || i.filename || '').toLowerCase().includes(term) ||
        (i.tags || []).some(t => t.toLowerCase().includes(term))
      );
    }
    
    // Sort by date
    return items.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [videos, images, audio, activeFilter, sourceFilter, searchTerm, frameworkFilter, scoreMinFilter, renderStatusFilter, dateFilter]);

  // Compute available frameworks from content
  const availableFrameworks = useMemo(() => {
    const all = [...videos, ...images, ...audio];
    const fws = new Set();
    all.forEach(i => { if (i.campaign_meta?.framework) fws.add(i.campaign_meta.framework); });
    return Array.from(fws).sort();
  }, [videos, images, audio]);

  // Compute vault badges across all content
  const vaultBadgeMap = useMemo(() => {
    return computeVaultBadges([...videos, ...images, ...audio]);
  }, [videos, images, audio]);


  return (
    <AppShell>



      <div className="p-5 sm:p-7 lg:p-10 max-w-7xl mx-auto">
        {/* Demo Mode Banner */}
        {isDemoMode && (
          <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl flex items-center gap-3" data-testid="library-demo-banner">
            <Eye className="w-4 h-4 text-amber-400 shrink-0" />
            <p className="text-xs text-amber-400 flex-1">Demo Mode — Showing curated campaign creatives. Your personal assets are hidden.</p>
          </div>
        )}
          
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10 animate-fadeInUp">
            <div>
              <p className="text-[10px] text-primary/60 uppercase tracking-[0.2em] font-semibold mb-3">Creative Vault</p>
              <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                All Generated Assets
              </h1>
              <p className="text-zinc-500 text-sm mt-2">
                {filteredContent.length} items
              </p>
              {/* Trust Badge */}
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 mt-2 rounded-full text-xs font-medium border text-blue-400 bg-blue-500/10 border-blue-500/20">
                <Lock className="w-3 h-3" />
                Your content is private
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className={`border-zinc-700 btn-press ${isSelecting ? 'bg-primary/10 text-primary border-primary' : ''}`}
                onClick={() => { setIsSelecting(!isSelecting); setSelectedItems([]); }}
              >
                {isSelecting ? <CheckSquare className="w-4 h-4 mr-2" /> : <Square className="w-4 h-4 mr-2" />}
                {isSelecting ? `${selectedItems.length} selected` : 'Select'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-zinc-700 btn-press"
                onClick={() => fetchLibrary()}
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800/50 p-4 mb-6 animate-fadeInUp delay-75" data-testid="filter-bar">
            {/* Type Filters */}
            <div className="flex flex-wrap gap-2 mb-4">
              {[
                { id: 'all', label: 'All', icon: LayoutGrid },
                { id: 'videos', label: 'Videos', icon: Video },
                { id: 'images', label: 'Images', icon: ImageIcon },
                { id: 'audio', label: 'Music', icon: Music }
              ].map(filter => (
                <button
                  key={filter.id}
                  onClick={() => setActiveFilter(filter.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    activeFilter === filter.id
                      ? 'bg-primary text-white'
                      : 'bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800 hover:text-white'
                  }`}
                  data-testid={`filter-${filter.id}`}
                >
                  <filter.icon className="w-4 h-4" />
                  {filter.label}
                </button>
              ))}
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  placeholder="Search content..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-zinc-800/50 border-zinc-700"
                  data-testid="search-input"
                />
              </div>
              
              {/* Source Filter Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="border-zinc-700 min-w-[140px] justify-between">
                    <span className="flex items-center gap-2">
                      <Filter className="w-4 h-4" />
                      {sourceFilter === 'all' ? 'All Sources' : 
                       sourceFilter === 'generated' ? 'Generated' :
                       sourceFilter === 'upload' ? 'Uploaded' :
                       sourceFilter === 'campaign' ? 'Campaign' : 'Enhanced'}
                    </span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-zinc-900 border-zinc-800">
                  <DropdownMenuItem onClick={() => setSourceFilter('all')}>All Sources</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSourceFilter('generated')}>Generated</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSourceFilter('upload')}>Uploaded</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSourceFilter('enhanced')}>Enhanced</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSourceFilter('campaign')}>Campaign</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Advanced Filters Toggle */}
              <Button
                variant="outline"
                className={`border-zinc-700 ${showAdvancedFilters ? 'bg-primary/10 text-primary border-primary' : ''}`}
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                data-testid="advanced-filters-toggle"
              >
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                Filters
                {(frameworkFilter !== 'all' || scoreMinFilter > 0 || renderStatusFilter !== 'all' || dateFilter !== 'all') && (
                  <span className="ml-1.5 w-5 h-5 rounded-full bg-primary text-white text-[10px] flex items-center justify-center">
                    {[frameworkFilter !== 'all', scoreMinFilter > 0, renderStatusFilter !== 'all', dateFilter !== 'all'].filter(Boolean).length}
                  </span>
                )}
              </Button>
            </div>

            {/* Advanced Filters Panel */}
            {showAdvancedFilters && (
              <div className="mt-4 pt-4 border-t border-zinc-800/50 grid grid-cols-2 sm:grid-cols-4 gap-3 animate-in fade-in duration-200" data-testid="advanced-filters-panel">
                {/* Framework Filter */}
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Framework</label>
                  <select
                    value={frameworkFilter}
                    onChange={(e) => setFrameworkFilter(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-xs text-zinc-200"
                    data-testid="filter-framework"
                  >
                    <option value="all">All Frameworks</option>
                    {availableFrameworks.map(fw => (
                      <option key={fw} value={fw}>{fw}</option>
                    ))}
                  </select>
                </div>

                {/* Score Min Filter */}
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">
                    Min Score: {scoreMinFilter > 0 ? scoreMinFilter : 'Any'}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="10"
                    value={scoreMinFilter}
                    onChange={(e) => setScoreMinFilter(parseInt(e.target.value))}
                    className="w-full accent-primary"
                    data-testid="filter-score"
                  />
                </div>

                {/* Render Status */}
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Render Status</label>
                  <select
                    value={renderStatusFilter}
                    onChange={(e) => setRenderStatusFilter(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-xs text-zinc-200"
                    data-testid="filter-render-status"
                  >
                    <option value="all">All</option>
                    <option value="ready">Video Ready</option>
                    <option value="rendering">Rendering</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>

                {/* Date Filter */}
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Date</label>
                  <select
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-xs text-zinc-200"
                    data-testid="filter-date"
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                  </select>
                </div>

                {/* Reset Filters */}
                {(frameworkFilter !== 'all' || scoreMinFilter > 0 || renderStatusFilter !== 'all' || dateFilter !== 'all') && (
                  <div className="col-span-full">
                    <button
                      onClick={() => { setFrameworkFilter('all'); setScoreMinFilter(0); setRenderStatusFilter('all'); setDateFilter('all'); }}
                      className="text-xs text-primary hover:underline"
                      data-testid="reset-filters-btn"
                    >
                      Reset all filters
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Bulk Actions Bar */}
          {isSelecting && selectedItems.length > 0 && (
            <div className="bg-primary/10 border border-primary/30 rounded-xl p-4 mb-6 flex flex-wrap items-center justify-between gap-4">
              <span className="text-sm font-medium text-white">
                {selectedItems.length} item{selectedItems.length > 1 ? 's' : ''} selected
              </span>
              <div className="flex items-center gap-2">
                {selectedItems.filter(s => s.media?.source === 'campaign').length >= 2 && (
                  <Button size="sm" className="bg-orange-500 hover:bg-orange-600" onClick={() => setShowCompareModal(true)} data-testid="compare-btn">
                    <Columns className="w-4 h-4 mr-2" />
                    Compare Creatives
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setShowBatchTagModal(true)}>
                  <Tag className="w-4 h-4 mr-2" />
                  Tag
                </Button>
                <Button size="sm" variant="outline" onClick={handleBulkDownload}>
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
                <Button size="sm" variant="destructive" onClick={handleBulkDelete}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          )}

          {/* Content Grid */}
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="aspect-[9/16] bg-zinc-800/50 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : filteredContent.length === 0 ? (
            <EmptyState filter={activeFilter} />
          ) : (
            <div 
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"
              data-testid="content-grid"
            >
              {filteredContent.map((item) => (
                <MediaCard
                  key={`${item.id}-${item.type}`}
                  media={item}
                  type={item.type}
                  isSelected={isSelected(item, item.type)}
                  isSelecting={isSelecting}
                  onSelect={toggleSelection}
                  onPreview={openPreview}
                  onDownload={handleDownload}
                  onDelete={handleDelete}
                  onRemix={handleRemix}
                  onShare={handleShare}
                  onRelight={openRelight}
                  vaultBadges={vaultBadgeMap[item.id]}
                  isDemoMode={isDemoMode}
                  onDuplicate={handleDuplicate}
                  onUseInCampaign={handleUseInCampaign}
                />
              ))}
            </div>
          )}
        </div>

      {/* Preview Modal */}
      <PreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        media={previewMedia}
        type={previewType}
        onDownload={handleDownload}
        onRemix={handleRemix}
        onShare={handleShare}
        onEdit={handleEdit}
      />

      {/* Batch Tag Modal */}
      <BatchTagModal
        open={showBatchTagModal}
        onClose={() => setShowBatchTagModal(false)}
        selectedItems={selectedItems}
        onTagsApplied={() => {
          setShowBatchTagModal(false);
          setSelectedItems([]);
          setIsSelecting(false);
          fetchLibrary(true);
        }}
      />

      {/* Campaign Compare Modal */}
      <CampaignCompareModal
        open={showCompareModal}
        onClose={() => setShowCompareModal(false)}
        items={selectedItems}
      />

      {/* Relight Quick-Action Modal */}
      {relightOpen && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !relightRunning && setRelightOpen(false)} data-testid="relight-modal">
          <div className="bg-[#12121a] border border-white/[0.08] rounded-2xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Sun className="w-5 h-5 text-orange-400" />
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Quick Relight</h2>
                <span className="text-[10px] uppercase tracking-wider text-orange-400/70 bg-orange-500/10 border border-orange-500/20 px-2 py-0.5 rounded ml-1">Powered by Nano Banana</span>
              </div>
              <button onClick={() => !relightRunning && setRelightOpen(false)} disabled={relightRunning} className="text-white/40 hover:text-white text-2xl leading-none w-8 h-8" data-testid="relight-modal-close">×</button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Source + preset picker */}
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Source</p>
                <img src={relightMedia?.primary_url || relightMedia?.image_url || relightMedia?.output_url} alt="source" className="w-full aspect-square object-cover rounded-lg border border-white/10 mb-4" />
                <p className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Lighting Preset</p>
                <div className="grid grid-cols-3 gap-2" data-testid="relight-modal-presets">
                  {relightPresets.map(p => (
                    <button key={p.id} onClick={() => setRelightActive(p.id)} disabled={relightRunning} data-testid={`modal-preset-${p.id}`}
                      className={`relative aspect-square rounded-md overflow-hidden border transition-all ${relightActive === p.id ? 'border-orange-500/70 ring-2 ring-orange-500/30' : 'border-white/[0.06] hover:border-white/20'}`}>
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${p.reference_url}`} alt={p.name} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                      <span className="absolute bottom-1 left-1.5 right-1.5 text-[10px] font-semibold text-white truncate">{p.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Output */}
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Relit Output</p>
                <div className="aspect-square rounded-lg border border-white/[0.06] bg-black/40 flex items-center justify-center overflow-hidden">
                  {relightRunning ? (
                    <div className="text-center" data-testid="relight-modal-running">
                      <Loader2 className="w-8 h-8 text-orange-400 animate-spin mx-auto mb-2" />
                      <p className="text-sm text-white/60">Relighting…</p>
                      <p className="text-[10px] text-white/30">10-30 seconds</p>
                    </div>
                  ) : relightResult?.output_url ? (
                    <img src={relightResult.output_url} alt="relit" className="w-full h-full object-cover" data-testid="relight-modal-result" />
                  ) : (
                    <div className="text-white/25 text-center text-sm">
                      <ImageIcon className="w-8 h-8 mx-auto mb-1" />
                      Pick a preset and click Relight
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-4">
                  <Button onClick={runRelight} disabled={relightRunning || !relightMedia} data-testid="relight-modal-run" className="flex-1 btn-oracle">
                    {relightRunning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Relighting</> : <><Sparkles className="w-4 h-4 mr-2" />Relight</>}
                  </Button>
                  {relightResult?.output_url && (
                    <Button variant="outline" onClick={() => { const a = document.createElement('a'); a.href = relightResult.output_url; a.target = '_blank'; a.download = 'relit.png'; a.click(); }} data-testid="relight-modal-download">
                      <Download className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
};

export default LibraryPage;
